import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { formatPhoneDisplay } from 'features/customers/helpers/formatting';

import {
  candidateLabel,
  MERGE_FIELDS,
  type MergeFieldKey,
  type WizardCandidate,
} from './merge-wizard-types';

// Only email/phone carry the unique indexes a merge frees on the retired
// record, so they are the values worth calling out: whichever the primary
// adopts move onto it, and the rest are released so they can be used elsewhere.
const ALIAS_FIELDS: Array<{ key: MergeFieldKey; label: string }> = [
  { key: 'email', label: 'email' },
  { key: 'phone', label: 'phone' },
];

const joinLabels = (items: string[]): string =>
  items.length > 1 ? `${items[0]} and ${items[1]}` : items[0];

const sentenceCase = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

export interface MergeReviewPreview {
  affectedRecordCount: number;
  exceedsCap: boolean;
  cap: number;
  crossType: boolean;
  touchesQuickbooks: boolean;
}

export interface MergeWizardReviewStepProps {
  candidates: WizardCandidate[];
  masterId: string;
  fieldSources: Record<MergeFieldKey, string>;
  preview: MergeReviewPreview | null;
  previewLoading: boolean;
  errorMessage: string | null;
  requireConsent: boolean;
  consent: boolean;
  onConsentChange: (value: boolean) => void;
}

export function MergeWizardReviewStep({
  candidates,
  masterId,
  fieldSources,
  preview,
  previewLoading,
  errorMessage,
  requireConsent,
  consent,
  onConsentChange,
}: MergeWizardReviewStepProps) {
  const byId = (id: string) => candidates.find((c) => c._id === id);
  const master = byId(masterId);
  const retired = candidates.filter((c) => c._id !== masterId);

  // What actually happens to a record being retired, in the admin's terms.
  const retiredEffects = (c: WizardCandidate): string[] => {
    const moving: string[] = [];
    const releasing: string[] = [];
    for (const f of ALIAS_FIELDS) {
      if (!(c[f.key] ?? '').toString().trim()) continue;
      if (fieldSources[f.key] === c._id) moving.push(f.label);
      else releasing.push(f.label);
    }

    const parts: string[] = [];
    if (moving.length) {
      const verb = moving.length > 1 ? 'move' : 'moves';
      parts.push(
        sentenceCase(`${joinLabels(moving)} ${verb} to the primary record`),
      );
    }
    if (releasing.length) {
      parts.push(sentenceCase(`${joinLabels(releasing)} will be released`));
    }
    if (c.hasLogin) parts.push('Login will be disabled');
    return parts;
  };

  const resultingValue = (key: MergeFieldKey): string => {
    const source = byId(fieldSources[key]) ?? master;
    const value = (source?.[key] ?? '').toString().trim();
    if (!value) return '—';
    return key === 'phone' ? formatPhoneDisplay(value) : value;
  };

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2">
        Review the primary record below. The other record(s) will be retired and
        their contact details and everything that referenced them will be
        reassigned to the primary record.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Chip label="Primary" color="primary" size="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            {master ? candidateLabel(master) : '—'}
          </Typography>
        </Stack>
        <Stack spacing={0.75}>
          {MERGE_FIELDS.map((f) => (
            <Box key={f.key} sx={{ display: 'flex', gap: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 110 }}
              >
                {f.label}
              </Typography>
              <Typography variant="body2">{resultingValue(f.key)}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Retiring {retired.length} record{retired.length === 1 ? '' : 's'}
        </Typography>
        <Stack spacing={0.75}>
          {retired.map((c) => {
            const effects = retiredEffects(c);
            return (
              <Box key={c._id}>
                <Typography variant="body2" color="text.secondary">
                  • {candidateLabel(c)}
                </Typography>
                {effects.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', pl: 1.5 }}
                  >
                    {effects.join(' · ')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {previewLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking records to reassign…
          </Typography>
        </Box>
      )}

      {!previewLoading && preview && !preview.exceedsCap && (
        <Alert severity="info">
          This merge will reassign {preview.affectedRecordCount} record
          {preview.affectedRecordCount === 1 ? '' : 's'} onto the primary
          record.
        </Alert>
      )}

      {!previewLoading && preview?.exceedsCap && (
        <Alert severity="warning">
          This merge would affect {preview.affectedRecordCount} records, which
          exceeds the {preview.cap}-record limit. Please contact support.
        </Alert>
      )}

      {!previewLoading && preview?.crossType && (
        <Alert severity="warning">
          These customers have different roles (e.g. producer and buyer).
          Merging them combines those roles onto the primary record.
        </Alert>
      )}

      {!previewLoading && preview?.touchesQuickbooks && (
        <Alert severity="info">
          A QuickBooks customer link will be reassigned. No changes are made in
          QuickBooks itself.
        </Alert>
      )}

      {requireConsent && (
        <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: 1 } }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={consent}
                onChange={(e) => onConsentChange(e.target.checked)}
              />
            }
            label="I understand the retired customer(s) will no longer be able to log in."
          />
        </Alert>
      )}

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </Stack>
  );
}
